import { useQueryClient } from '@tanstack/react-query';
import { useBlocker } from '@tanstack/react-router';
import { toast } from 'react-toastify';
import {
  getGetTuberculosisHistoryQueryKey,
  useGetTuberculosisHistorySuspense,
  useUpdateTuberculosisHistory,
} from '../../../../api/generated/contact-tracing-controller/contact-tracing-controller.ts';
import { LeavePageWarning } from '../../../../components/LeavePageWarning.tsx';
import { useAutoSaveForm } from '../../../../hooks/useAutoSaveForm.ts';
import {
  TuberculosisHistoryFormFields,
  getTuberculosisHistoryDefaultValues,
  mapTuberculosisHistoryFormValuesToPayload,
  tuberculosisHistoryFieldMetaInfos,
} from './TuberculosisHistoryFormFields.tsx';

export function EditTuberculosisHistoryForm({ tracingContactId }: { tracingContactId: string }) {
  const queryClient = useQueryClient();
  const { data: tuberculosisHistory } = useGetTuberculosisHistorySuspense(tracingContactId);

  const updateTuberculosisHistoryMutation = useUpdateTuberculosisHistory({
    mutation: {
      onSuccess: (data) => {
        queryClient.setQueryData(getGetTuberculosisHistoryQueryKey(tracingContactId), data);
      },
      onError: () =>
        toast.error('Beim speicher der Krankengeschichte ist ein Fehler aufgetreten', { toastId: 'saveError' }),
    },
  });

  const {
    registerWithSaveOnBlur,
    control,
    getFieldState,
    watch,
    formState: { isFormDirty },
    scheduleSave,
    handleSaveOnBlur,
  } = useAutoSaveForm({
    sourceData: tuberculosisHistory,
    getDefaultValues: getTuberculosisHistoryDefaultValues,
    save: (data) =>
      updateTuberculosisHistoryMutation.mutateAsync({
        id: tracingContactId,
        data: mapTuberculosisHistoryFormValuesToPayload(data, {
          id: tuberculosisHistory.id,
          patientId: tracingContactId,
        }),
      }),
    fieldMetaInfos: tuberculosisHistoryFieldMetaInfos,
    saveMode: 'blur',
    onValidationFailed: () =>
      toast.warn('Krankengeschichte kann aufgrund eines Fehlers momentan nicht gespeichert werden', {
        toastId: 'validationError',
        autoClose: false,
      }),
    onValidationPassed: () => toast.dismiss('validationError'),
  });

  const blocker = useBlocker({
    shouldBlockFn: () => isFormDirty && !updateTuberculosisHistoryMutation.isPending,
    withResolver: true,
  });

  return (
    <>
      <TuberculosisHistoryFormFields
        registerWithSaveOnBlur={registerWithSaveOnBlur}
        control={control}
        watch={watch}
        getFieldState={getFieldState}
        handleSaveOnBlur={handleSaveOnBlur}
      />
      <LeavePageWarning
        open={blocker.status === 'blocked'}
        onCancel={() => blocker.reset}
        onDelete={() => blocker.proceed}
        onSave={() => {
          if (blocker.reset) {
            blocker.reset();
          }
          scheduleSave();
        }}
        title={`Daten zur Krankengeschichte behalten?`}
      />
    </>
  );
}
